import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import { clearFirestoreCache } from '../../utils/clearFirestoreCache';

function isNativePlatform() {
  try {
    const platform = Capacitor.getPlatform();
    return platform === 'android' || platform === 'ios';
  } catch {
    return false;
  }
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Solo loguear en desarrollo
    if (import.meta.env.DEV) {
      console.error('🔴 Error capturado por ErrorBoundary:', error);
      console.error('📍 Stack:', errorInfo.componentStack);
    }

    // En producción, enviar a analytics si está disponible
    if (!import.meta.env.DEV && typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'exception', {
        description: error.message,
        fatal: true
      });
    }

    // Auto-recovery: si Firestore queda en un estado inesperado (caché/IndexedDB corrupto)
    // limpiar el cache una vez por sesión y recargar.
    try {
      const msg = error?.message || '';
      const isFirestoreInternalAssertion =
        msg.includes('FIRESTORE') &&
        msg.includes('INTERNAL ASSERTION FAILED') &&
        (msg.includes('(ID: ca9)') || msg.includes('(ID: b815)') || msg.includes('Unexpected state'));

      if (isFirestoreInternalAssertion) {
        const key = 'firestore_internal_assertion_recovered';
        if (sessionStorage.getItem(key) !== 'true') {
          sessionStorage.setItem(key, 'true');
          clearFirestoreCache().finally(() => {
            try {
              window.dispatchEvent(
                new CustomEvent('app:reload-required', {
                  detail: { reason: 'Firestore internal assertion (cache cleared)' },
                })
              );
            } catch {
            }
          });
        }
      }
    } catch {
      // ignore
    }

    this.setState({
      error,
      errorInfo
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const showTechnicalDetails = import.meta.env.DEV || isNativePlatform();

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
            <div className="text-6xl mb-4">😞</div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Algo salió mal
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Lo sentimos, ocurrió un error inesperado. Intenta recargar la página.
            </p>
            
            {showTechnicalDetails && this.state.error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded text-left">
                <p className="text-sm font-mono text-red-800 dark:text-red-300 break-all">
                  {this.state.error.message}
                </p>
                {this.state.error.stack && (
                  <pre className="mt-2 text-[10px] font-mono text-red-800/90 dark:text-red-300/90 whitespace-pre-wrap break-words">
                    {this.state.error.stack}
                  </pre>
                )}
                {this.state.errorInfo?.componentStack && (
                  <pre className="mt-2 text-[10px] font-mono text-red-800/90 dark:text-red-300/90 whitespace-pre-wrap break-words">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleReload}
              >
                Recargar página
              </Button>
              <Button
                variant="outlined"
                onClick={this.handleGoHome}
              >
                Ir al inicio
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
